import type { Dictionary } from './ru';

export const en: Dictionary = {
  common: {
    appName: 'miroom',
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    saved: 'Saved',
    delete: 'Delete',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    back: 'Back',
    search: 'Search',
    more: 'More',
    done: 'Done',
    or: 'or',
  },
  errors: {
    network: 'Cannot reach the server. Check your connection.',
    internal: 'Something went wrong. Please try again.',
    unauthorized: 'Please sign in.',
    forbidden: 'You do not have permission.',
    not_found: 'Not found.',
    rate_limited: 'Too many attempts. Wait a minute.',
    validation: 'Please check the entered data.',
    link_invalid: 'The link has expired or was already used. Request a new one.',
    mail_failed: "We couldn't send the email. Please try again later.",
    payload_too_large: 'The file is too large.',
    unsupported_media: 'Unsupported file format.',
    conflict: 'Data conflict.',
  },
  theme: { label: 'Theme', system: 'System', light: 'Light', dark: 'Dark' },
  locale: { label: 'Language', ru: 'Русский', en: 'English' },
  nav: {
    login: 'Sign in',
    register: 'Sign up',
    logout: 'Sign out',
    boards: 'My boards',
    settings: 'Profile & settings',
    startFree: 'Start for free',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: "We'll email you a sign-in link — no password needed",
    registerTitle: 'Create your account',
    registerSubtitle: 'Free, with unlimited boards',
    name: 'Name',
    namePlaceholder: 'What should we call you',
    email: 'Email',
    emailHint: "We'll send a sign-in link to this address",
    submitLogin: 'Email me a sign-in link',
    submitRegister: 'Create account',
    noAccount: 'No account yet?',
    haveAccount: 'Already have an account?',
    toRegister: 'Sign up',
    toLogin: 'Sign in',
    nameRequired: 'Enter your name',
    emailInvalid: 'Enter a valid email',
    asideTitle: 'Think together on an infinite board',
    asideText: 'Sticky notes, diagrams, mind maps, voting and live cursors for your whole team.',
    note1: '💡 Ideas',
    note2: "🗳 Let's vote!",
    note3: '🚀 Launch',
    checkEmailTitle: 'Check your email',
    checkEmailText: "We've just sent a unique sign-in link to",
    noLink: 'Not receiving the link?',
    resend: 'Send link again',
    resendIn: 'Send again in {n}s',
    noLinkHint: '. Check your spam folder. The link works once and expires in 30 minutes.',
    linkResent: 'Link sent again',
    changeEmail: 'Use a different email',
    linkInvalidTitle: "Couldn't sign you in",
    requestNewLink: 'Request a new link',
  },
  dashboard: {
    title: 'Boards',
    newBoard: 'New board',
    searchPlaceholder: 'Search boards',
    import: 'Import from file',
    importFailed: 'Import failed: the file is not a miroom board',
    tabs: {
      recent: 'Recent',
      mine: 'My boards',
      shared: 'Shared with me',
      starred: 'Starred',
      trash: 'Trash',
    },
    sort: 'Sort',
    sortDate: 'By date',
    sortName: 'By name',
    quickStart: 'Quick start',
    allTemplates: 'All templates',
    edited: 'Edited {time}',
    deletedAt: 'Deleted {time}',
    by: 'Owner: {name}',
    star: 'Star',
    unstar: 'Unstar',
    actions: 'Board actions',
    open: 'Open',
    openNewTab: 'Open in new tab',
    rename: 'Rename',
    duplicate: 'Duplicate',
    duplicated: 'Created a copy of “{title}”',
    moveToTrash: 'Move to trash',
    leave: 'Leave board',
    restore: 'Restore',
    deleteForever: 'Delete forever',
    movedToTrash: 'Board moved to trash',
    left: 'You left the board',
    undo: 'Undo',
    restored: 'Board restored',
    deleteForeverTitle: 'Delete forever?',
    deleteForeverText: 'Board “{title}” and its history will be deleted permanently.',
    renameTitle: 'Rename board',
    boardName: 'Name',
    trashHint: 'Boards in the trash are deleted forever after 30 days.',
    loadError: 'Could not load boards',
    empty: {
      recent: 'Boards you open will appear here',
      mine: 'You have no boards yet',
      shared: 'Nobody has shared a board with you yet',
      starred: 'Star boards to find them quickly',
      trash: 'Trash is empty',
      search: 'Nothing found for “{q}”',
      cta: 'Create your first board — it takes a second',
    },
  },
  templates: {
    title: 'Create a board',
    subtitle: 'Start from scratch or from a template',
    creating: 'Creating board…',
    blank: { name: 'Blank board', desc: 'An infinite canvas for any idea' },
    retro: { name: 'Retrospective', desc: 'Start / Stop / Continue' },
    kanban: { name: 'Kanban', desc: 'Task columns from backlog to done' },
    mindmap: { name: 'Mind map', desc: 'Branching ideas around a central topic' },
    storymap: { name: 'User Story Map', desc: 'User journey and stories by release' },
    swot: { name: 'SWOT analysis', desc: 'Strengths, weaknesses, opportunities, threats' },
    lean: { name: 'Lean Canvas', desc: 'A business model on one page' },
    roadmap: { name: 'Roadmap', desc: 'Initiatives by quarter and team' },
    flowchart: { name: 'Flowchart', desc: 'A process with decisions and branches' },
    brainstorm: { name: 'Brainstorm', desc: 'A question in the middle, ideas around it' },
    weekly: { name: 'Weekly plan', desc: 'Days of the week for personal or team plans' },
  },
  time: { justNow: 'just now' },
  board: {
    loading: 'Loading board…',
    connecting: 'Connecting…',
    offline: 'You are offline. Changes will sync when the connection is back.',
    readOnly: 'View only',
    commentOnly: 'Comment only',
    notFoundTitle: 'Board not found',
    notFoundText: 'It may have been deleted or the link is wrong.',
    noAccessTitle: 'No access to this board',
    noAccessText: 'Ask the owner to invite you or to enable link access.',
    loginTitle: 'Sign in to open this board',
    loginText: 'This board is available to members only.',
    toDashboard: 'Go to my boards',
    emptyHint: 'Double-click for a sticky note, or pick a tool on the left.',
    untitled: 'Untitled',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomReset: 'Zoom to 100%',
    zoomFit: 'Fit to screen',
    backToBoards: 'Back to boards',
    guestName: 'Guest',
    editText: 'Text editor',
    cardTitle: 'Task title',
    cardDescription: 'Description',
    uploading: 'Uploading image… {pct}%',
    dropHere: 'Drop to add images',
    canvasLabel: 'Board canvas',
    viewerHint: 'This board is empty.',
    renameBoard: 'Rename board',
  },
  tools: {
    label: 'Tools',
    select: 'Select (V)',
    hand: 'Hand (H, Space)',
    sticky: 'Sticky note (N)',
    text: 'Text (T)',
    shape: 'Shape (S)',
    connector: 'Connection line (L)',
    pen: 'Pen (P)',
    marker: 'Highlighter',
    eraser: 'Eraser (E)',
    frame: 'Frame (F)',
    image: 'Image',
    card: 'Card',
    table: 'Table',
    mindmap: 'Mind map (M)',
    comment: 'Comment (C)',
    emoji: 'Emoji',
    laser: 'Laser pointer (K)',
    more: 'More tools',
    undo: 'Undo (Ctrl+Z)',
    redo: 'Redo (Ctrl+Shift+Z)',
    penColor: 'Color',
    penWidth: 'Width',
    frameDefault: 'Frame',
    stickyColor: 'Sticky color',
    shapes: {
      rect: 'Rectangle',
      roundRect: 'Rounded',
      ellipse: 'Ellipse',
      diamond: 'Diamond',
      triangle: 'Triangle',
      parallelogram: 'Parallelogram',
      star: 'Star',
      hexagon: 'Hexagon',
      arrowBlock: 'Arrow',
      cylinder: 'Cylinder',
      callout: 'Callout',
    },
  },
  props: {
    label: 'Object properties',
    color: 'Color',
    fill: 'Fill',
    stroke: 'Border',
    textColor: 'Text color',
    transparent: 'No fill',
    fontSize: 'Font size',
    auto: 'Auto',
    font: 'Font',
    fonts: { sans: 'Sans', serif: 'Serif', mono: 'Monospace', hand: 'Handwriting' },
    bold: 'Bold (Ctrl+B)',
    italic: 'Italic (Ctrl+I)',
    underline: 'Underline (Ctrl+U)',
    strike: 'Strikethrough',
    alignLeft: 'Align left',
    alignCenter: 'Align center',
    alignRight: 'Align right',
    strokeWidth: 'Line width',
    strokeStyle: 'Line style',
    solid: 'Solid',
    dashed: 'Dashed',
    dotted: 'Dotted',
    shape: 'Shape type',
    textSettings: 'Text',
    route: 'Line type',
    routes: { straight: 'Straight', elbow: 'Elbow', curved: 'Curved' },
    startCap: 'Line start',
    endCap: 'Line end',
    caps: {
      none: 'None',
      arrow: 'Arrow',
      triangle: 'Triangle',
      circle: 'Circle',
      diamond: 'Diamond',
    },
    swapEnds: 'Reverse direction',
    cardColor: 'Card color',
    assignee: 'Assignee',
    dueDate: 'Due date',
    addRow: 'Add row',
    addColumn: 'Add column',
    removeRow: 'Remove row',
    removeColumn: 'Remove column',
    headerRow: 'Header row',
    addChild: 'Add child node (Tab)',
    addSibling: 'Add sibling node (Enter)',
    collapse: 'Collapse branch',
    expand: 'Expand branch',
    lock: 'Lock',
    unlock: 'Unlock',
    delete: 'Delete (Del)',
    edit: 'Edit text',
  },
  menu: {
    bringToFront: 'Bring to front',
    bringForward: 'Bring forward',
    sendBackward: 'Send backward',
    sendToBack: 'Send to back',
    group: 'Group',
    ungroup: 'Ungroup',
    lock: 'Lock',
    unlock: 'Unlock',
    align: 'Align',
    alignLeft: 'Align left',
    alignHCenter: 'Align center',
    alignRight: 'Align right',
    alignTop: 'Align top',
    alignVMiddle: 'Align middle',
    alignBottom: 'Align bottom',
    distributeH: 'Distribute horizontally',
    distributeV: 'Distribute vertically',
    delete: 'Delete',
    copy: 'Copy',
    cut: 'Cut',
    paste: 'Paste',
    duplicate: 'Duplicate',
    selectAll: 'Select all',
    snapToGrid: 'Snap to grid',
    snapToGridOff: 'Snap to grid (turn on the grid in the board menu)',
    copyLink: 'Link to object',
  },
  presence: {
    online: 'On this board now',
    you: 'You',
    follow: 'Follow {name}',
    following: 'You are following {name}',
    stopFollowing: 'Stop',
    summon: 'Bring everyone here',
    summoned: '{name} brought everyone to their view',
    summonSent: 'Everyone was brought to your view',
    react: 'React',
    chatPlaceholder: 'Say something… (Enter)',
    connected: 'Connected',
    connecting: 'Connecting…',
    offline: 'Offline',
  },
  comments: {
    title: 'Comments',
    placeholder: 'Write a comment…',
    replyPlaceholder: 'Reply…',
    send: 'Send',
    resolve: 'Resolve',
    reopen: 'Reopen',
    delete: 'Delete',
    deleteThread: 'Delete thread',
    showResolved: 'Show resolved',
    empty: 'No comments yet. Pick the Comment tool (C) and click on the board.',
    open: 'Open',
    resolved: 'Resolved',
    replies: { one: '{n} reply', few: '{n} replies', many: '{n} replies', other: '{n} replies' },
    pin: 'Comment by {name}',
    newPin: 'New comment',
  },
  facilitation: {
    timer: 'Timer',
    timerPresets: 'Start timer',
    minutes: { one: '{n} minute', few: '{n} minutes', many: '{n} minutes', other: '{n} minutes' },
    pause: 'Pause',
    resume: 'Resume',
    plusMinute: '+1 minute',
    stop: 'Stop',
    timeUp: "Time's up!",
    voting: 'Voting',
    startVoting: 'Start voting',
    votesPerUser: 'Votes per participant',
    duration: 'Duration',
    noLimit: 'No limit',
    votingHint: 'Participants click objects to vote. Click again to take a vote back.',
    votesLeft: {
      one: '{n} vote left',
      few: '{n} votes left',
      many: '{n} votes left',
      other: '{n} votes left',
    },
    endVoting: 'End voting',
    results: 'Voting results',
    voters: {
      one: '{n} participant voted',
      few: '{n} participants voted',
      many: '{n} participants voted',
      other: '{n} participants voted',
    },
    noVotes: 'No votes yet',
    closeResults: 'Close results',
    votesExhausted: 'No votes left — click a voted object again to take a vote back',
    votingStarted: 'Voting started: click objects to vote',
    object: 'Object',
  },
  present: {
    frames: 'Frames',
    start: 'Present',
    startFrom: 'Present from this frame',
    empty: 'No frames on this board. Add a frame (F) to present the board as slides.',
    prev: 'Previous (←)',
    next: 'Next (→)',
    exit: 'Exit (Esc)',
    moveUp: 'Move up',
    moveDown: 'Move down',
    counter: '{i} of {n}',
  },
  share: {
    button: 'Share',
    title: 'Share board',
    linkAccess: 'Link access',
    linkNone: 'Only invited people',
    linkView: 'Anyone with the link can view',
    linkComment: 'Anyone with the link can comment',
    linkEdit: 'Anyone with the link can edit',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    invite: 'Invite by email',
    inviteButton: 'Invite',
    invited: '{name} now has access',
    userNotFound: 'No miroom user with this email',
    ownerAlready: 'This is the board owner',
    members: 'Members',
    roles: { owner: 'Owner', editor: 'Editor', commenter: 'Commenter', viewer: 'Viewer' },
    remove: 'Remove access',
    you: 'you',
    readOnlyHint: 'Only the owner and editors can manage access.',
  },
  guest: {
    title: 'What should we call you?',
    text: 'Other people on the board will see your name.',
    name: 'Name',
    join: 'Join board',
    or: 'Have an account?',
    login: 'Sign in',
    banner: 'You are a guest. Sign in to keep this board in your list.',
  },
  revoked: {
    title: 'Access to this board was revoked',
    text: 'The owner changed access settings or the board was deleted.',
  },
  versions: {
    title: 'Version history',
    save: 'Save version',
    namePlaceholder: 'Version name',
    auto: 'Autosave',
    beforeRestore: 'Before restore',
    restore: 'Restore',
    restoreConfirm: 'The board will return to “{name}”. The current state will be kept in history.',
    restored: 'Version restored',
    saved: 'Version saved',
    empty: 'No versions yet. They are created automatically while you work.',
    preview: 'Preview',
    loadError: 'Could not load history',
  },
  boardMenu: {
    label: 'Board menu',
    history: 'Version history',
    grid: 'Show grid',
    exportPng: 'Export as PNG',
    exportSelection: 'Export selection as PNG',
    exportSvg: 'Export as SVG',
    exportPdf: 'Export as PDF (frames)',
    exportJson: 'Backup (JSON)',
    exporting: 'Preparing file…',
    exportEmpty: 'The board is empty — nothing to export',
    duplicate: 'Duplicate board',
    duplicated: 'Copy created',
  },
  search: {
    title: 'Search board',
    placeholder: 'Find text on the board',
    empty: 'Nothing found',
    types: {
      sticky: 'Sticky note',
      text: 'Text',
      shape: 'Shape',
      connector: 'Line',
      drawing: 'Drawing',
      image: 'Image',
      frame: 'Frame',
      card: 'Card',
      table: 'Table',
      mindmap: 'Mind map',
      emoji: 'Emoji',
    },
    hint: 'Searches sticky notes, text, shapes, cards, tables and frames.',
    count: { one: '{n} match', few: '{n} matches', many: '{n} matches', other: '{n} matches' },
  },
  minimap: { label: 'Minimap', show: 'Show minimap', hide: 'Hide minimap' },
  help: {
    title: 'Keyboard shortcuts',
    button: 'Keyboard shortcuts (?)',
    groups: { tools: 'Tools', edit: 'Editing', view: 'Navigation', collab: 'Collaboration' },
    items: {
      select: 'Select',
      hand: 'Hand / pan',
      sticky: 'Sticky note',
      text: 'Text',
      shape: 'Shape',
      rect: 'Rectangle',
      ellipse: 'Ellipse',
      line: 'Line',
      pen: 'Pen',
      eraser: 'Eraser',
      frame: 'Frame',
      mindmap: 'Mind map',
      comment: 'Comment',
      laser: 'Laser',
      undo: 'Undo',
      redo: 'Redo',
      copy: 'Copy',
      paste: 'Paste',
      duplicate: 'Duplicate',
      delete: 'Delete',
      selectAll: 'Select all',
      group: 'Group',
      ungroup: 'Ungroup',
      lock: 'Lock',
      front: 'Bring to front',
      back: 'Send to back',
      nudge: 'Nudge (Shift — by 10)',
      edit: 'Edit text',
      mmChild: 'Mind map: child / sibling',
      pan: 'Pan the board',
      zoom: 'Zoom',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      zoom100: 'Zoom to 100%',
      fit: 'Fit to screen',
      search: 'Search the board',
      chat: 'Cursor chat',
      help: 'This help',
      cancel: 'Cancel / deselect',
    },
  },
  landing: {
    title: 'Think together on an infinite board',
    subtitle:
      'miroom is a free online whiteboard for teams: sticky notes, diagrams, mind maps, voting and live cursors. Nothing to install, unlimited boards.',
    cta: 'Start for free',
    ctaBoards: 'Open my boards',
    login: 'I have an account',
    why: {
      title: 'Why miroom',
      formulaLabel: 'Miro plus Room equals miroom',
      text: 'The name echoes Miro, and Room is just that — a room. miroom is your shared workroom: open a board, bring in your team and think together as if around one table.',
    },
    featuresTitle: 'Everything you need to collaborate',
    features: {
      realtime: {
        title: 'Real-time collaboration',
        text: 'Cursors, edits and selections of everyone appear instantly — no conflicts, no reloads.',
      },
      tools: {
        title: 'All the tools',
        text: 'Sticky notes, shapes, lines, drawing, tables, cards, mind maps and images.',
      },
      facilitation: {
        title: 'Workshops without chaos',
        text: 'Voting, timer, follow mode, reactions and frame-by-frame presentations.',
      },
      templates: {
        title: 'Ready-made templates',
        text: 'Retro, kanban, user story map, SWOT, lean canvas, roadmap and more.',
      },
      share: {
        title: 'Easy sharing',
        text: 'Share a link — guests join without signing up. Roles and email invites.',
      },
      export: {
        title: 'Export and history',
        text: 'PNG, SVG, PDF and backups. Version history with one-click restore.',
      },
    },
    templatesTitle: 'Start from a template',
    bottomTitle: 'Bring your team to one board',
    bottomText: 'Creating a board takes a second.',
    footer: 'miroom — a free alternative to online whiteboards',
    demo: { idea: 'Idea!', vote: "Let's vote", launch: 'Launch on Friday', plan: 'Sprint plan' },
  },
  settings: {
    title: 'Profile & settings',
    profile: 'Profile',
    name: 'Name',
    color: 'Avatar color',
    email: 'Email',
    save: 'Save',
    saved: 'Profile saved',
    appearance: 'Appearance',
    emailMismatch: "The email doesn't match your account",
    sessions: 'Sessions',
    sessionsText: 'Sign out of your account on every device and browser, including this one.',
    logoutAll: 'Sign out everywhere',
    danger: 'Delete account',
    dangerText: 'Your account and all your boards will be deleted. This cannot be undone.',
    deleteAccount: 'Delete account',
    deleteConfirmTitle: 'Delete your account forever?',
    deleteConfirmText: 'Enter your account email to confirm.',
    back: 'Back to boards',
  },
  crash: {
    title: 'Something went wrong',
    text: 'Reload the page — your boards are saved.',
    reload: 'Reload page',
  },
  notFound: {
    title: 'Page not found',
    text: 'The link may be outdated or mistyped.',
    home: 'Go home',
  },
};
